import { MapPin, Phone, Mail, Globe, MessageCircle, Send } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BRAND } from '@/data/content';
import { DEGREE_LEVELS, COURSES, COUNTRIES } from '@/components/CounsellingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionHeader = ({ children }) => (
  <div style={{
    fontSize: '11px',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: 'var(--gold)',
    fontWeight: 600,
    margin: '8px 0 -4px 0',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-subtle)',
  }}>
    {children}
  </div>
);

const ContactPage = () => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', city: '',
    degree_level: '', course: '', country: 'Italy', message: '', source: 'contact_page',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/counselling`, form);
      toast.success('Message sent! We will respond within 24-48 hours.');
      setForm({
        full_name: '', email: '', phone: '', city: '',
        degree_level: '', course: '', country: 'Italy', message: '', source: 'contact_page',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ paddingTop: '80px' }} data-testid="contact-page">
      <section>
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow">Get In Touch</div>
            <h2 className="section-title">Let's Build Your <span className="gold-gradient">Global Future</span></h2>
            <p className="section-subtitle">
              Reach out to us for personalized guidance on your study abroad journey. Our admissions team is ready to help.
            </p>
          </div>

          <div className="contact-page-grid">
            <div className="contact-info-card">
              <h3 style={{ marginBottom: '24px', color: 'var(--gold)' }}>Contact Information</h3>

              <div className="contact-info-item">
                <div className="contact-info-icon"><MapPin size={18} /></div>
                <div>
                  <div className="contact-info-label">Office Address</div>
                  <div className="contact-info-value">
                    Aazam Colony, Dargah Road,<br />
                    Parbhani, Maharashtra 431401,<br />
                    India
                  </div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon"><Phone size={18} /></div>
                <div>
                  <div className="contact-info-label">Phone Numbers</div>
                  <div className="contact-info-value">
                    {BRAND.phones.map((p, i) => (
                      <div key={i}><a href={`tel:${p.replace(/\s/g, '')}`}>{p}</a></div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon"><Mail size={18} /></div>
                <div>
                  <div className="contact-info-label">Email</div>
                  <div className="contact-info-value">
                    <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>
                  </div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon"><Globe size={18} /></div>
                <div>
                  <div className="contact-info-label">Website</div>
                  <div className="contact-info-value">
                    <a href="https://www.euroelite.in" target="_blank" rel="noopener noreferrer">{BRAND.website}</a>
                  </div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon"><MessageCircle size={18} /></div>
                <div>
                  <div className="contact-info-label">WhatsApp</div>
                  <div className="contact-info-value">
                    <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noopener noreferrer">{BRAND.phoneDisplay}</a>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '30px', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                <iframe
                  title="EuroElite Office - Aazam Colony, Parbhani"
                  src="https://www.google.com/maps?q=EuroElite+Education+Consultancy,+Dargah+Road,+Aazam+Colony,+Parbhani,+Maharashtra+431401&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg)' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                ></iframe>
                <a
                  href="https://maps.app.goo.gl/129dx97dn9wyQesy7"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', padding: '12px', textAlign: 'center', background: 'rgba(212, 168, 73, 0.08)', color: 'var(--gold)', fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}
                  data-testid="open-in-google-maps"
                >
                  Open in Google Maps →
                </a>
              </div>
            </div>

            <div className="contact-info-card">
              <h3 style={{ marginBottom: '24px', color: 'var(--gold)' }}>Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="form-group" data-testid="contact-form">

                <SectionHeader>Personal Information</SectionHeader>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">Full Name *</label>
                    <input type="text" name="full_name" required value={form.full_name} onChange={handleChange} className="form-input" placeholder="Your name" data-testid="contact-name" />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Phone *</label>
                    <input type="tel" name="phone" required value={form.phone} onChange={handleChange} className="form-input" placeholder="+91 ..." data-testid="contact-phone" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">Email *</label>
                    <input type="email" name="email" required value={form.email} onChange={handleChange} className="form-input" placeholder="you@email.com" data-testid="contact-email" />
                  </div>
                  <div className="form-field">
                    <label className="form-label">City *</label>
                    <input type="text" name="city" required value={form.city} onChange={handleChange} className="form-input" placeholder="Your city" data-testid="contact-city" />
                  </div>
                </div>

                <SectionHeader>Academic Information</SectionHeader>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">Degree Level *</label>
                    <select name="degree_level" required value={form.degree_level} onChange={handleChange} className="form-select" data-testid="contact-degree">
                      <option value="">Select degree</option>
                      {DEGREE_LEVELS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Course *</label>
                    <select name="course" required value={form.course} onChange={handleChange} className="form-select" data-testid="contact-course">
                      <option value="">Select course</option>
                      {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <SectionHeader>Destination</SectionHeader>
                <div className="form-field">
                  <label className="form-label">Country *</label>
                  <select name="country" required value={form.country} onChange={handleChange} className="form-select" data-testid="contact-country">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <SectionHeader>Additional Information</SectionHeader>
                <div className="form-field">
                  <label className="form-label">Message (Optional)</label>
                  <textarea name="message" rows="4" value={form.message} onChange={handleChange} className="form-textarea" placeholder="How can we help you?" data-testid="contact-message" />
                </div>

                <button type="submit" disabled={submitting} className="form-submit" data-testid="contact-submit">
                  {submitting ? 'Sending...' : (<>Send Message <Send size={14} /></>)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
